// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo, useCallback, useState } from 'react';
import { AnalyzerOutput, ActionOutput, ObservableOutput } from 'thehive-sdk';
import { useLocalMessage, useLocalDico, classList } from '@Helpers/';
import { AnalyzerAnalysis } from '@Organisms/AnalyzerTable/AnalyzerAnalysis/AnalyzerAnalysis';
import { ColumnsType } from 'antd/lib/table';
import THTable from '@Molecules/THTable/THTable';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import AnalyzerActions from '@Organisms/AnalyzerTable/AnalyzerActions/AnalyzerActions';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ICONS } from '@Constants/iconsApp';

import './AnalyzerTable.scss';

interface DynamicObjectType<T> {
    [attribute: string]: T;
}

interface AnalyzerNameProps {
    onClick(): void;
    name: string;
    extend: boolean;
    hidden?: boolean;
}

const AnalyzerName = ({
    extend = false,
    name = 'no name',
    hidden = false,
    onClick,
}: AnalyzerNameProps): ReactElement => (
    <div className="analyzer-name">
        <span className={classList('analyzer-name-extend', { minus: extend, plus: !extend, hidden })} onClick={onClick}>
            <ICONS.PlusSquareOutlined className="plus-square" />
            <ICONS.MinusSquareOutlined className="minus-square" />
        </span>
        <span>{name}</span>
    </div>
);

interface AnalyzerTableProps {
    analyzers: AnalyzerOutput[] | undefined;
    observableId: string;
    analysis: ActionOutput[] | undefined;
    loading: boolean;
    observable?: ObservableOutput;
    eventList: ObservableEventList;
    hasPermissions?: boolean;
    importObservable?: boolean;
    visibleReportId?: string;
}

const AnalyzerTable = ({
    observable,
    analyzers,
    observableId,
    analysis = [],
    loading,
    eventList,
    hasPermissions = false,
    importObservable = true,
    visibleReportId,
}: AnalyzerTableProps): ReactElement => {
    const i = useLocalMessage();

    const columnsTrad = useLocalDico(
        [
            'analyzer.table.column.name',
            'analyzer.table.column.analysis',
            'analyzer.table.column.actions',
            'analyzer.table.column.empty',
        ],
        i,
    );

    const dictionaryAnalysis = useMemo(
        () =>
            analysis.reduce((acc: DynamicObjectType<ActionOutput[]>, c: ActionOutput) => {
                const name = (c?.analyzerName === '-' ? c?.analyzerId : c?.analyzerName) || '--';
                if (!acc[name]) {
                    acc[name] = [];
                }
                acc[name].push(c);
                return acc;
            }, {}),
        [analysis],
    );

    const activedAnalyzers = useMemo((): AnalyzerOutput[] => {
        if (analyzers?.length) {
            return analyzers;
        }
        return Object.values(
            analysis.reduce<Record<string, any>>((acc, curr) => {
                const name = curr?.analyzerName || '--';
                if (!acc[name]) {
                    acc[name] = {
                        name,
                        cortexIds: [curr.cortexId],
                    };
                } else {
                    acc[name] = {
                        ...acc[name],
                        cortexIds: [...acc[name].cortexIds, curr.cortexId],
                    };
                }

                return acc;
            }, {}),
        ).sort((a, b) => a.name.localeCompare(b.name));
    }, [analysis, analyzers]);

    const [extended, setExtended] = useState<string[]>([]);

    const toggle = useCallback(
        (name: string): void => {
            if (!extended.includes(name)) {
                setExtended([...extended, name]);
            } else {
                setExtended(extended.filter((e) => e !== name));
            }
        },
        [extended, setExtended],
    );

    const columns = useMemo(() => {
        const columns: ColumnsType<AnalyzerOutput> = [
            {
                title: <ColumnTitle>{columnsTrad['analyzer.table.column.name']}</ColumnTitle>,
                className: 'analyzer-table__column__name custom-table__cell',
                render: (_: unknown, { name }): ReactElement => {
                    const listAnalysis = dictionaryAnalysis[name];
                    const hidden = !listAnalysis || listAnalysis.length <= 1;

                    return (
                        <AnalyzerName
                            extend={extended.includes(name)}
                            name={name}
                            hidden={hidden}
                            onClick={(): void => toggle(name)}
                        />
                    );
                },
            },
            {
                title: <ColumnTitle>{columnsTrad['analyzer.table.column.analysis']}</ColumnTitle>,
                className: 'analyzer-table__column__analysis custom-table__cell',

                render: (_: unknown, { name }): ReactElement | null =>
                    dictionaryAnalysis[name] ? (
                        <AnalyzerAnalysis
                            observable={observable}
                            name={name}
                            importObservable={importObservable}
                            extend={extended.includes(name)}
                            analysis={dictionaryAnalysis[name]}
                            eventList={eventList}
                            visibleReportId={visibleReportId}
                        />
                    ) : (
                        <i>{columnsTrad['analyzer.table.column.empty']}</i>
                    ),
            },
        ];

        if (analyzers?.length && hasPermissions) {
            columns.push({
                className: 'analyzer-table__column__actions custom-table__cell',
                render: (_, { name, cortexIds }: AnalyzerOutput): ReactElement => {
                    const listAnalysis = dictionaryAnalysis[name];
                    const firstUsed = !listAnalysis || listAnalysis.length === 0;
                    const loadingAnalysis = listAnalysis?.find(
                        ({ analyzerId, status }) => analyzerId === name && status === 'Waiting',
                    );

                    return (
                        <AnalyzerActions
                            name={name}
                            cortexIds={cortexIds}
                            observableId={observableId}
                            firstUsed={firstUsed}
                            loadingAnalysis={loadingAnalysis}
                        />
                    );
                },
            });
        }

        return columns;
    }, [columnsTrad, dictionaryAnalysis, extended, toggle, observableId, observable, eventList]); //eslint-disable-line

    return <THTable dataSource={activedAnalyzers} columns={columns} rowKey="id" loading={loading} />;
};

export default AnalyzerTable;
